import { ref, type Ref } from "vue";

/**
 * 🔴 **A NEWER BUILD IS SITTING THERE, WAITING** — the rail's NEW VERSION pill.
 *
 * A single-page app that has been open since Tuesday is running Tuesday's code. The service worker
 * has already fetched Thursday's and parked it, because activating it under a person's hands would
 * swap the JavaScript out from beneath a half-typed form. So the browser waits — silently, forever,
 * until every tab of the origin closes.
 *
 * "Forever" is the problem. On a production desk a tab stays open for weeks, which means a fix can
 * ship on Monday and reach nobody until they happen to restart their browser. **This makes the wait
 * visible and gives the person the button.** It never reloads by itself: the moment to lose an
 * unsaved take is theirs to choose, not the deploy's.
 */

/** True once a build newer than the running one is installed and waiting. */
export const newVersionReady: Ref<boolean> = ref(false);

let waitingWorker: ServiceWorker | null = null;
let reloading = false;

/** The waiting worker, for the test. */
export function waitingVersion(): ServiceWorker | null {
  return waitingWorker;
}

function remember(worker: ServiceWorker | null): void {
  waitingWorker = worker;
  newVersionReady.value = worker !== null;
}

/**
 * Watch a registration for a build that is ready and waiting.
 *
 * Three ways one arrives, and all three happen in practice:
 *   - it was ALREADY waiting when this page loaded (the person reloaded a tab that had one parked);
 *   - one finishes installing while the page is open (`updatefound` → `installed`);
 *   - a periodic check finds one — a desk that is never reloaded would otherwise never ask.
 *
 * 🔴 `installed` **with a controller** is the test that matters. Without a controller the worker is
 * the FIRST one for this origin: nothing is being replaced, the page is already running the code it
 * just cached, and announcing a new version would be announcing itself.
 */
export function watchForNewVersion(registration: ServiceWorkerRegistration, everyMs = 30 * 60 * 1000): () => void {
  const controlled = (): boolean => navigator.serviceWorker?.controller != null;

  if (registration.waiting && controlled()) remember(registration.waiting);

  const onUpdateFound = (): void => {
    const installing = registration.installing;
    if (installing === null) return;
    const onStateChange = (): void => {
      if (installing.state === "installed" && controlled()) remember(installing);
      if (installing.state === "redundant") installing.removeEventListener("statechange", onStateChange);
    };
    installing.addEventListener("statechange", onStateChange);
  };
  registration.addEventListener("updatefound", onUpdateFound);

  // A tab open for a week never navigates, so nothing would ever ask the server whether there is
  // something newer. Half-hourly is far below any deploy cadence and costs one conditional GET.
  const timer = window.setInterval(() => void registration.update().catch(() => undefined), everyMs);

  return () => {
    registration.removeEventListener("updatefound", onUpdateFound);
    window.clearInterval(timer);
  };
}

/**
 * Take the new build: tell the waiting worker to stop waiting, and reload once it has taken control.
 *
 * 🔴 The reload is driven by `controllerchange` rather than fired immediately. Reloading first would
 * race the activation and could land the person right back on the old build — the failure that makes
 * a person press the button twice and conclude it does nothing.
 *
 * `reloading` guards the case where the worker was already activating: `controllerchange` can fire
 * more than once, and two reloads in flight is a blank screen.
 */
export function applyNewVersion(reload: () => void = () => window.location.reload()): void {
  const worker = waitingWorker;
  if (worker === null) return;
  navigator.serviceWorker?.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    reload();
  });
  worker.postMessage({ type: "SKIP_WAITING" });
}

/** Test seam — forgets any waiting build and the reload guard. */
export function resetNewVersion(): void {
  remember(null);
  reloading = false;
}
